import React, { useState, useEffect } from 'react';
import {
  Box, Button, FormControl, InputLabel, Select, MenuItem, TextField,
  Typography, Alert, Card, CardContent, Grid, Avatar, IconButton
} from '@mui/material';
import {
  DatePicker, LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarToday, EventAvailable, LocalHospital, MoneyOff, DateRange,
  Person as PersonIcon, UploadFile, Close
} from '@mui/icons-material';
import {
  createLeave, getLeaveBalance, getEmployeeLeaveBalance,
  createLeaveById, addLeaveJustificationByHR, addLeaveJustification
} from '../../services/leavesAPIs';
import LeaveCalendar from '../../components/calendars/CalendarCard';
import LeaveBalanceCard from '../../components/cards/BalanceCard';
import { enGB } from 'date-fns/locale';
import { getEmployeeImage } from '../../services/HRstaff';

const LeaveRequestForm = ({ lb, id, onSubmitSuccess, employees, IsEmployee, fetchData }) => {
  const [formData, setFormData] = useState({
    type: '',
    startDate: null,
    endDate: null,
    justification: '',
  });
  const [justificationFile, setJustificationFile] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(null);

  // === FETCH EMPLOYEE IMAGE ===
  useEffect(() => {
    const fetchImage = async () => {
      if (id && employees) {
        const emp = employees.find(e => e.employeeId === id);
        if (emp?.image) {
          try {
            const url = await getEmployeeImage(id);
            setProfileImageUrl(url);
          } catch (err) {
            console.error('Failed to load image:', err);
            setProfileImageUrl(null);
          }
        } else {
          setProfileImageUrl(null);
        }
      }
    };
    fetchImage();
  }, [id, employees]);

  // Cleanup image URL
  useEffect(() => {
    return () => {
      if (profileImageUrl) URL.revokeObjectURL(profileImageUrl);
    };
  }, [profileImageUrl]);

  // === FETCH LEAVE BALANCE ===
  const fetchLeaveBalance = async () => {
    // Prevent calling API with null id when handleWithEmployeeId is active
    if (lb === 'handleWithEmployeeId' && !id) {
      setLeaveBalance(null);
      return;
    }

    try {
      let data;
      if (lb === 'handleWithEmployeeId') {
        data = await getEmployeeLeaveBalance(id);
      } else {
        data = await getLeaveBalance();
      }
      setLeaveBalance(data);
      console.log('Fetched Leave Balance (Employee View):', data);
    } catch (error) {
      console.error('Failed to fetch leave balance', error);
    }
  };

  useEffect(() => {
    fetchLeaveBalance();
  }, [id, lb]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (name) => (date) => {
    setFormData({ ...formData, [name]: date });
  };

  const handleCalendarDateSelect = (date) => {
    if (!formData.startDate || (formData.startDate && formData.endDate)) {
      setFormData({ ...formData, startDate: date, endDate: null });
    } else if (formData.startDate && !formData.endDate) {
      if (date >= formData.startDate) {
        setFormData({ ...formData, endDate: date });
      } else {
        setFormData({ ...formData, startDate: date, endDate: null });
      }
    }
  };

  const selectedEmployee = employees?.find(emp => emp.employeeId === id);
  const employeeName = selectedEmployee
    ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : id ? `Employee ID: ${id}` : 'Select an employee';

  const validateForm = () => {
    if (!id && lb === 'handleWithEmployeeId') {
      setError('Please select an employee.');
      return false;
    }
    if (!formData.type || !formData.startDate || !formData.endDate || !formData.justification) {
      setError('All fields are required');
      return false;
    }
    if (IsEmployee && formData.startDate < new Date()) {
      setError('Start date cannot be in the past');
      return false;
    }
    if (formData.endDate < formData.startDate) {
      setError('End date cannot be before start date');
      return false;
    }
    return true;
  };

  const formatDateOnly = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    const FD = {
      ...formData,
      startDate: formatDateOnly(formData.startDate),
      endDate: formatDateOnly(formData.endDate),
    };

    try {
      let createdLeave;
      if (lb === 'handleWithEmployeeId' && id) {
        const payload = {
          ...FD,
          employee: { employeeId: id },
        };
        createdLeave = await createLeaveById(payload);
      } else {
        createdLeave = await createLeave(FD);
      }

      // Upload justification file if needed
      if (formData.type === 'UNPAID_LEAVE' && justificationFile) {
        try {
          if (IsEmployee) {
            await addLeaveJustification(createdLeave, justificationFile);
          } else {
            await addLeaveJustificationByHR(createdLeave, justificationFile);
          }
        } catch (uploadErr) {
          console.error('Justification upload failed:', uploadErr);
          setError('Leave created, but file upload failed. Please retry.');
        }
      }

      setSuccess('Leave request submitted successfully');
      setFormData({ type: '', startDate: null, endDate: null, justification: '' });
      setJustificationFile(null);
      fetchData?.();
      await fetchLeaveBalance();
      if (onSubmitSuccess) {
        setTimeout(() => onSubmitSuccess(), 500);
      }
    } catch (err) {
      const msg = err.response?.data;
      if (msg === 'Leave request conflicts with existing request') {
        setError('You already have a leave during this period.');
      } else if (msg === 'Insufficient leave balance') {
        setError('You do not have enough leave balance for this request.');
      } else if (msg === 'Start date cannot be in the past') {
        setError('Start date cannot be in the past');
      } else {
        setError('Failed to submit leave request. Please try again.');
      }
      console.error('Submission error:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJustificationFile(file);
    }
  };

  const removeFile = () => {
    setJustificationFile(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <Grid container justifyContent="center" spacing={3}>
          {/* Leave Balance Card */}
          <Grid item xs={12} md={12} lg={12}>
            <LeaveBalanceCard leaveBalance={leaveBalance} />
          </Grid>

          {/* Main Form */}
          <Grid item xs={12} md={8} lg={7}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                overflow: 'visible',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: '#228be6',
                      fontWeight: 'bold',
                      fontSize: '1.2rem',
                    }}
                  >
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Employee"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setProfileImageUrl(null)}
                      />
                    ) : selectedEmployee ? (
                      `${selectedEmployee.firstName[0]}${selectedEmployee.lastName[0]}`.toUpperCase()
                    ) : (
                      <PersonIcon />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#212529">
                      Leave Request
                    </Typography>
                    {id && (
                      <Typography variant="body2" color="text.secondary">
                        {employeeName}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Alerts */}
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                    {success}
                  </Alert>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  {/* Leave Type */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Leave Type</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      label="Leave Type"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="ANNUAL_LEAVE">Annual Leave</MenuItem>
                      <MenuItem value="UNPAID_LEAVE">Unpaid Leave</MenuItem>
                      <MenuItem value="MATERNITY_LEAVE">Maternity Leave</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Date Pickers */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Start Date"
                        value={formData.startDate}
                        onChange={handleDateChange('startDate')}
                        slotProps={{ textField: { fullWidth: true } }}
                        format="dd/MM/yyyy"
                        minDate={IsEmployee ? new Date() : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="End Date"
                        value={formData.endDate}
                        onChange={handleDateChange('endDate')}
                        slotProps={{ textField: { fullWidth: true } }}
                        format="dd/MM/yyyy"
                        minDate={formData.startDate || (IsEmployee ? new Date() : undefined)}
                      />
                    </Grid>
                  </Grid>

                  {/* Justification */}
                  <TextField
                    name="justification"
                    label="Justification"
                    value={formData.justification}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ mb: 3 }}
                  />

                  {/* File Upload (Unpaid Leave) */}
                  {formData.type === 'UNPAID_LEAVE' && (
                    <Box sx={{ mb: 3 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<UploadFile />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          py: 1.5,
                          borderColor: '#228be6',
                          color: '#228be6',
                          '&:hover': { borderColor: '#1c7ed6', bgcolor: '#f0f5ff' }
                        }}
                      >
                        {justificationFile ? justificationFile.name : 'Upload Justification Document'}
                        <input
                          type="file"
                          hidden
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                      </Button>
                      {justificationFile && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {justificationFile.name}
                          </Typography>
                          <IconButton size="small" onClick={removeFile}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Submit Button */}
                  <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    size="large"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      bgcolor: '#228be6',
                      '&:hover': { bgcolor: '#1c7ed6' },
                    }}
                  >
                    Submit Leave Request
                  </Button>
                </form>
              </Box>
            </Card>
          </Grid>

          {/* Calendar */}
          <Grid item xs={12} md={4} lg={5}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                height: 'fit-content',
                p: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, pl: 1 }}>
                Select Dates
              </Typography>
              <LeaveCalendar
                startDate={formData.startDate}
                endDate={formData.endDate}
                onDateSelect={handleCalendarDateSelect}
                highlightRange
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};
export default LeaveRequestForm;

// import React, { useState, useEffect } from 'react';
// import {
//    Box, Button, FormControl, InputLabel, Select, MenuItem, TextField,
//   Typography, Alert, Card, CardContent, Grid, Chip,Paper,
// } from '@mui/material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { createLeave, getLeaveBalance, getEmployeeLeaveBalance, createLeaveById, addLeaveJustificationByHR, addLeaveJustification  } from '../../services/leavesAPIs';
// import { CalendarToday, EventAvailable, LocalHospital, MoneyOff, DateRange } from '@mui/icons-material';
// import LeaveCalendar from '../../components/calendars/CalendarCard';
// import LeaveBalanceCard from '../../components/cards/BalanceCard';
// import { enGB } from 'date-fns/locale'; // or fr, nl, etc.



// const LeaveRequestForm = ({lb, id, onSubmitSuccess, employees, IsEmployee, fetchData}) => {
//   const [formData, setFormData] = useState({
//     type: '',
//     startDate: null,
//     endDate: null,
//     justification: '',
//   });
//   const [justificationFile, setJustificationFile] = useState(null);
//   const [leaveBalance, setLeaveBalance] = useState(null);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');

//   // Fetch leave balance on component mount
//   const fetchLeaveBalance = async () => {
//     try {
//       let data;
//       if (lb =='handleWithEmployeeId') {
//         data = await getEmployeeLeaveBalance(id);
//       } else {
//         data = await getLeaveBalance();
//       }
//         setLeaveBalance(data);
//       } catch (error) {
//         console.error('Failed to fetch leave balance', error);
//       }
//   };
     
//   useEffect(() => {
//     fetchLeaveBalance();
//   }, [id]);


//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const handleDateChange = (name) => (date) => {
//     setFormData({ ...formData, [name]: date });
//   };
  
//   const handleCalendarDateSelect = (date) => {
//     if (!formData.startDate || (formData.startDate && formData.endDate)) {
//       // Set start date if none selected, or reset both if both are selected
//       setFormData({ ...formData, startDate: date, endDate: null });
//     } else if (formData.startDate && !formData.endDate) {
//       // Set end date if start date is already selected
//       if (date >= formData.startDate) {
//         setFormData({ ...formData, endDate: date });
//       } else {
//         // If selected date is before start date, make it the new start date
//         setFormData({ ...formData, startDate: date, endDate: null });
//       }
//     }
//   };


//   const selectedEmployee = employees?.find(emp => emp.employeeId === id);
//   const employeeName = selectedEmployee
//     ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
//     : !employees
//       ? 'Loading employee...'
//       : id
//         ? `Employee ID: ${id}`
//         : 'Select an employee';

//   const validateForm = () => {

//     if (!id && lb === 'handleWithEmployeeId') {
//       setError('Please select an employee.');
//       return false;
//     }
//     if (!formData.type || !formData.startDate || !formData.endDate || !formData.justification) {
//       setError('All fields are required');
//       return false;
//     }
//     if (IsEmployee && formData.startDate < new Date()) {
//       setError('Start date cannot be in the past');
//       return false;
//     }
//     if (IsEmployee && formData.endDate < formData.startDate) {
//       setError('End date cannot be before start date');
//       return false;
//     }
//     return true;
//   };

//   const formatDateOnly = (date) => {
//     if (!date) return null;
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is 0-based
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
//   };
  
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
//     setSuccess('');
//     if (!validateForm()) return;

//     const FD = {
//       ...formData,
//       startDate: formatDateOnly(formData.startDate),
//       endDate: formatDateOnly(formData.endDate),
//     };
//     console.log('Payload being sent:', FD); 

//     try {
//       let createdLeave;
//       if (lb === 'handleWithEmployeeId' && id) {
//         const payload = {
//           ...FD,
//           employee: {
//             employeeId: id,
//           },
//         };
//       createdLeave = await createLeaveById(payload);
//       console.log('Leave request instance created:', createdLeave);

//       }
//       else{
//         createdLeave = await createLeave(formData);
//         console.log('Leave request instance created:', createdLeave);
//       }
      

//       // Now handle justification file upload if needed
//     if (formData.type === 'UNPAID_LEAVE' && justificationFile) {
//       try {
//         // Determine which API to use based on role (HR vs Employee)
//         // Assuming `IsEmployee` tells us the current user role
//         if (IsEmployee) {
//           await addLeaveJustification(createdLeave, justificationFile);
//         } else {
//           await addLeaveJustificationByHR(createdLeave, justificationFile);
//         }
//       } catch (uploadErr) {
//         console.error('Justification upload failed:', uploadErr);
//         // Optionally show warning but don't fail the whole request
//         setError('Leave created, but justification upload failed. Please retry.');
//       }
//     }
      
//       //onSubmitSuccess();
//       setSuccess('Leave request submitted successfully');
//       setFormData({ type: '', startDate: null, endDate: null, justification: '' });
//       fetchData();
//       if (onSubmitSuccess) {
//         setTimeout(() => {
//         onSubmitSuccess();
//       }, 500);
//       }
//       await fetchLeaveBalance();
//     } catch (err) {
//       //setError(err.response?.data?.message || 'Failed to submit leave request');
//       if (err.response?.status === 400) {
//         if (err.response.data === 'Leave request conflicts with existing request') {
//           setError('You already have a leave during this period.');
//         } else if (err.response.data === 'Insufficient leave balance') {
//           setError('You do not have enough leave balance for this request.');
//         } else if (err.response.data === 'Start date cannot be in the past') {
//           setError('Start date cannot be in the past');
//         } else {
//           setError('Failed to submit leave request. Please try again.');
//         }
//       } else {
//         setError('Something went wrong. Please try again.');
//       }
//       console.error('Submission error:', err);
//     }
//   };


//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
//       <Box sx={{ p: 3, width: '100%', height: '100%'}}>
//         {leaveBalance && <LeaveBalanceCard leaveBalance={leaveBalance} />}

//         <Grid container spacing={3} >
//         <Grid item xs={12} md={6} sx={{ width: '100%' }}>
//         <Card sx={{ height: 'fit-content', boxShadow: 3, maxWidthS: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',padding: 2 }}>
//           <CardContent sx={{ p: 3 , width: '100%'}}>
//             <Typography variant="h5" gutterBottom sx={{ fontSize:'1.2rem', display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
//             <DateRange color="primary" />
//             Leave Request Form
//             </Typography>

//         {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
//         {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
       
//         <form onSubmit={handleSubmit}>
//           {id && (
//             <TextField
//               label="Employee"
//               value={employeeName}
//               fullWidth
//               disabled
//               sx={{ mb: 2 }}
//             />
//           )}
//           <FormControl fullWidth sx={{ mb: 2 }}>
//             <InputLabel>Leave Type</InputLabel>
//             <Select name="type" value={formData.type} onChange={handleChange}>
//               <MenuItem value="ANNUAL_LEAVE">Annual leave</MenuItem>
//               <MenuItem value="UNPAID_LEAVE">Unpaid leave</MenuItem>
//               {/* <MenuItem value="SICK_LEAVE">Sick leave</MenuItem> */}
//               <MenuItem value="MATERNITY_LEAVE">Maternity leave</MenuItem>
//             </Select>
//           </FormControl>
//           <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
//             <DatePicker
//             size="small"
//               label="Start Date"
//               value={formData.startDate}
//               onChange={handleDateChange('startDate')}
//               renderInput={(params) => <TextField {...params} fullWidth />}
//               minDate={IsEmployee ? new Date() : undefined}
//               format="dd/MM/yyyy"
//             />
//             <DatePicker
//               label="End Date"
//               value={formData.endDate}
//               onChange={handleDateChange('endDate')}
//               renderInput={(params) => <TextField {...params} fullWidth />}
//               minDate={IsEmployee ? (formData.startDate || new Date()) : formData.startDate || undefined}
//               format="dd/MM/yyyy"
//             />
//           </Box>
//           <TextField
//             name="justification"
//             label="Justification"
//             multiline
//             rows={4}
//             fullWidth
//             value={formData.justification}
//             onChange={handleChange}
//             sx={{ mb: 2 }}
//           />
//           {formData.type === 'UNPAID_LEAVE' && (
//             <Box sx={{ mb: 2 }}>
//               <Button
//                 variant="outlined"
//                 component="label"
//                 fullWidth
//                 sx={{ justifyContent: 'flex-start' }}
//               >
//                 {justificationFile ? justificationFile.name : 'Upload Justification Document'}
//                 <input
//                   type="file"
//                   hidden
//                   onChange={(e) => setJustificationFile(e.target.files[0] || null)}
//                   accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
//                 />
//               </Button>
//               {justificationFile && (
//                 <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
//                   Selected: {justificationFile.name}
//                 </Typography>
//               )}
//             </Box>
//           )}
//           <Button
//             type="submit"
//             variant="contained"
//             sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1e88e5' } }}
//           >
//             Submit Request
//           </Button>

//         </form>
//         </CardContent>
      
//         <LeaveCalendar
//           startDate={formData.startDate}
//           endDate={formData.endDate}
//           onDateSelect={handleCalendarDateSelect}
//         />

//       </Card>
//       </Grid>
//       </Grid>
//       </Box>
  
//     </LocalizationProvider>
//   );
// };

// export default LeaveRequestForm;
